// KiraAvatar.jsx — Friendly Bot avatar at Personality C ("Full Character") level.
// Pure React + inline SVG/CSS. 8 emotional states + idle micro-expressions
// + hover-notice + typing detection.
//
// Sibling to (not replacement for) src/components/chat/KiraAvatar.jsx —
// the chat panel keeps the rich matrix avatar; the floater uses this one.

import React, { useEffect, useRef, useState } from "react";

const NEON       = "#00FF99";
const RED        = "#ff4d4d";
const PINK       = "rgba(255,138,188,0.85)";
const NATIVE     = 80;
const VALID_EMOS = [
  "neutral", "thinking", "happy", "alert", "error",
  "loading", "listening", "celebrating", "excited",
];

// Map any of the 12 chat-emotion tags + new orchestrator tags down to the 8
// floater states. Used by useKira.js.
export function mapEmotion(tag, text = "") {
  const t  = (tag  || "").toLowerCase();
  const tx = (text || "").toLowerCase();

  // Hard error / failed states
  if (t.includes("error")    || t.includes("fail")
   || tx.includes("sorry")   || tx.includes("trouble")) return "error";

  // Anything action-prefixed needs user confirmation → alert
  if (t.startsWith("action_"))                          return "alert";

  // Warning-class signals
  if (t.includes("urgent")   || t.includes("concerned")
   || t.includes("warning")  || t.includes("risk")
   || t.includes("deadline") || t.includes("safety")
   || t.includes("high_risk"))                          return "alert";

  // Thinking / clarifying / processing
  if (t.includes("think")    || t.includes("process")
   || t.includes("confus")   || t.includes("question")
   || t.includes("clarify")  || t.includes("wellbeing")) return "thinking";

  // Positive — completion, greetings, celebrations
  if (t.includes("happy")    || t.includes("excit")
   || t.includes("celebrat") || t.includes("empathy")
   || t.includes("success")  || t.includes("complete")
   || t.includes("greeting") || t.includes("welcome")
   || t === "policy_reference")                         return "happy";

  if (t.includes("load"))                               return "loading";

  return "neutral";
}

export default function KiraAvatar({ emotion = "neutral", size = 56, isTyping = false }) {
  // typing forces the listening state regardless of the emotion prop
  const requested = isTyping ? "listening" : emotion;
  const safe      = VALID_EMOS.includes(requested) ? requested : "neutral";
  const scale     = size / NATIVE;

  const [isHovered,    setIsHovered]    = useState(false);
  const [transientCls, setTransientCls] = useState("");

  // ── Frequent idle (4-7s) — only when neutral, not hovered, not typing
  useEffect(() => {
    if (safe !== "neutral" || isHovered) return;
    const ACTS = ["kfb-wiggle", "kfb-glance-l", "kfb-glance-r", "kfb-scan"];

    let pendingClear;
    const fire = () => {
      const cls = ACTS[Math.floor(Math.random() * ACTS.length)];
      setTransientCls(cls);
      // wiggle is short; glance/scan are longer
      const dur = cls === "kfb-wiggle" ? 700 : cls === "kfb-scan" ? 2400 : 1500;
      pendingClear = setTimeout(() => setTransientCls(""), dur);
    };

    const tick = () => {
      fire();
      next = setTimeout(tick, 4000 + Math.random() * 3000);
    };
    let next = setTimeout(tick, 4000 + Math.random() * 3000);

    return () => { clearTimeout(next); clearTimeout(pendingClear); };
  }, [safe, isHovered]);

  // ── Rare idle (15-20s) — yawn or antenna spiral, 50/50
  useEffect(() => {
    if (safe !== "neutral" || isHovered) return;

    let pendingClear;
    const fire = () => {
      const cls = Math.random() < 0.5 ? "kfb-yawn" : "kfb-spiral";
      setTransientCls(cls);
      pendingClear = setTimeout(() => setTransientCls(""), 2400);
    };

    const tick = () => {
      fire();
      next = setTimeout(tick, 15000 + Math.random() * 5000);
    };
    let next = setTimeout(tick, 15000 + Math.random() * 5000);

    return () => { clearTimeout(next); clearTimeout(pendingClear); };
  }, [safe, isHovered]);

  // Conditional decorations
  const showSparkles = safe === "happy" || safe === "celebrating" || safe === "excited";
  const showHeart    = safe === "happy";
  const showCheck    = safe === "celebrating";

  return (
    <div
      style={{
        width:        size,
        height:       size,
        display:      "inline-grid",
        placeItems:   "center",
        overflow:     "visible",
        pointerEvents:"auto",
        position:     "relative",
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      aria-hidden="true"
    >
      <div
        style={{
          width:           NATIVE,
          height:          NATIVE,
          transform:       `scale(${scale})`,
          transformOrigin: "center center",
          position:        "relative",
        }}
      >
        {showSparkles && (
          <>
            <span className="kfb-sparkle kfb-sparkle-1" />
            <span className="kfb-sparkle kfb-sparkle-2" />
            <span className="kfb-sparkle kfb-sparkle-3" />
          </>
        )}
        {showHeart && <span className="kfb-heart">♥</span>}
        {showCheck && <span className="kfb-check">✓</span>}

        <div className={`kfb kfb-${safe} ${transientCls} ${isHovered ? "is-noticed" : ""}`.trim()}>
          <div className="kfb-antenna">
            <span className="kfb-antenna-dot" />
            <span className="kfb-antenna-ping" />
          </div>
          <div className="kfb-face">
            <span className="kfb-blush kfb-blush-l" />
            <span className="kfb-blush kfb-blush-r" />
            <div className="kfb-eyes">
              <span className="kfb-eye kfb-eye-l" />
              <span className="kfb-eye kfb-eye-r" />
            </div>
            <div className="kfb-mouth" />
          </div>
        </div>
      </div>

      <style>{KFB_CSS}</style>
    </div>
  );
}

const KFB_CSS = `
.kfb {
  width: 64px; height: 64px;
  background: #6db069;
  border: 2px solid ${NEON};
  border-radius: 16px;
  box-shadow: 0 0 12px rgba(0,255,153,0.36);
  position: absolute; top: 8px; left: 8px;
  display: grid; place-items: center;
  transition: border-color .3s ease, box-shadow .3s ease, transform .3s ease;
  animation:
    kfb-bob 3.4s ease-in-out infinite,
    kfb-breathe 4.5s ease-in-out infinite;
}
@keyframes kfb-bob {
  0%,100% { transform: translateY(0); }
  50%     { transform: translateY(-2.5px); }
}
@keyframes kfb-breathe {
  0%,100% { box-shadow: 0 0 12px rgba(0,255,153,0.36); }
  50%     { box-shadow: 0 0 22px rgba(0,255,153,0.50); }
}

/* ── Hover "notice" ── */
.kfb.is-noticed {
  transform: scale(1.06);
  border-color: ${NEON};
  box-shadow: 0 0 24px rgba(0,255,153,0.5);
}
.kfb.is-noticed .kfb-eye  { width: 10px; height: 10px; }
.kfb.is-noticed .kfb-antenna {
  animation: none;
  transform: translateX(-50%) rotate(0deg) translateY(-2px);
}

/* ── Idle modifiers (applied to .kfb root) ── */
.kfb.kfb-wiggle .kfb-antenna { animation: kfb-wiggle 700ms ease-in-out !important; }
@keyframes kfb-wiggle {
  0%   { transform: translateX(-50%) rotate(0); }
  20%  { transform: translateX(-50%) rotate(-15deg); }
  40%  { transform: translateX(-50%) rotate(12deg); }
  60%  { transform: translateX(-50%) rotate(-8deg); }
  80%  { transform: translateX(-50%) rotate(5deg); }
  100% { transform: translateX(-50%) rotate(0); }
}
.kfb.kfb-glance-l .kfb-eye { animation: kfb-glance-l 1.5s ease-in-out !important; }
.kfb.kfb-glance-r .kfb-eye { animation: kfb-glance-r 1.5s ease-in-out !important; }
@keyframes kfb-glance-l { 0%,100%{transform:translateX(0);} 20%,55%{transform:translateX(-2px);} }
@keyframes kfb-glance-r { 0%,100%{transform:translateX(0);} 20%,55%{transform:translateX(2px);} }

.kfb.kfb-scan .kfb-eye { animation: kfb-scan 2.4s ease-in-out !important; }
@keyframes kfb-scan {
  0%   { transform: translateX(0); }
  25%  { transform: translateX(-2.5px); }
  50%  { transform: translateX(0); }
  75%  { transform: translateX(2.5px); }
  100% { transform: translateX(0); }
}

.kfb.kfb-yawn { animation: kfb-yawn 2.4s ease-in-out !important; }
@keyframes kfb-yawn {
  0%   { transform: translateY(0)    scale(1); }
  20%  { transform: translateY(2px)  scale(0.97); }
  50%  { transform: translateY(2px)  scale(0.97); }
  80%  { transform: translateY(-3px) scale(1.04); }
  100% { transform: translateY(0)    scale(1); }
}
.kfb.kfb-yawn .kfb-eye { animation: kfb-yawn-eye 2.4s ease-in-out !important; }
@keyframes kfb-yawn-eye {
  0%,100% { transform: scaleY(1); }
  20%,50% { transform: scaleY(0.2); }
}
.kfb.kfb-yawn .kfb-antenna { animation: kfb-yawn-antenna 2.4s ease-in-out !important; }
@keyframes kfb-yawn-antenna {
  0%,100% { transform: translateX(-50%) rotate(0deg); }
  20%,50% { transform: translateX(-50%) rotate(40deg); }
}

.kfb.kfb-spiral .kfb-antenna { animation: kfb-spiral 1.8s ease-in-out !important; }
@keyframes kfb-spiral {
  0%   { transform: translateX(-50%) rotate(0); }
  100% { transform: translateX(-50%) rotate(360deg); }
}

/* ── Antenna ── */
.kfb-antenna {
  position: absolute; top: -10px; left: 50%;
  transform: translateX(-50%);
  transform-origin: bottom center;
  width: 1.5px; height: 8px;
  background: ${NEON};
  animation: kfb-sway 3.2s ease-in-out infinite;
}
@keyframes kfb-sway {
  0%,100% { transform: translateX(-50%) rotate(-7deg); }
  50%     { transform: translateX(-50%) rotate(7deg); }
}
.kfb-antenna-dot {
  position: absolute; top: -4px; left: 50%; transform: translateX(-50%);
  width: 5px; height: 5px; border-radius: 50%;
  background: ${NEON};
  box-shadow: 0 0 6px rgba(0,255,153,0.80);
  animation: kfb-dot 2.4s ease-in-out infinite;
}
@keyframes kfb-dot {
  0%,100% { opacity: 1; }
  50%     { opacity: 0.55; }
}
.kfb-antenna-ping { display: none; }

/* ── Face ── */
.kfb-face {
  width: 46px; height: 38px;
  background: #0d0d0d;
  border-radius: 10px;
  box-shadow: inset 0 0 6px rgba(0,0,0,0.60);
  display: flex; flex-direction: column; align-items: center;
  justify-content: center; gap: 4px;
  position: relative;
}
.kfb-eyes { display: flex; gap: 8px; }
.kfb-eye {
  width: 8px; height: 8px; border-radius: 50%;
  background: #00FF99;
  box-shadow: 0 0 8px rgba(0,255,153,0.72);
  display: inline-block;
  position: relative;
  transform-origin: center;
  transition: all .25s ease;
}
.kfb-mouth {
  width: 14px; height: 2px;
  background: #00FF99;
  border-radius: 2px;
  box-shadow: 0 0 4px rgba(0,255,153,0.48);
}
.kfb-blush { display: none; }

/* ============== NEUTRAL ============== */
.kfb-neutral .kfb-eye { animation: kfb-blink 4.5s ease-in-out infinite; }
@keyframes kfb-blink {
  0%,92%,100% { transform: scaleY(1); }
  95%,98%     { transform: scaleY(0.1); }
}

/* ============== THINKING ============== */
.kfb-thinking {
  animation:
    kfb-bob 3.4s ease-in-out infinite,
    kfb-breathe 4.5s ease-in-out infinite,
    kfb-tilt 2.4s ease-in-out infinite;
}
@keyframes kfb-tilt {
  0%,100% { rotate: -8deg; }
  50%     { rotate:  8deg; }
}
.kfb-thinking .kfb-eye { animation: kfb-wander 2s ease-in-out infinite; }
@keyframes kfb-wander {
  0%,100% { transform: translate(-1.5px, -1px); }
  50%     { transform: translate( 2.5px, -1px); }
}
.kfb-thinking .kfb-mouth {
  width: 8px; height: 4px; background: transparent;
  border: 1.5px solid #00FF99; border-bottom: none;
  border-radius: 6px 6px 0 0;
}
.kfb-thinking .kfb-face::after {
  content: "•••";
  position: absolute; bottom: -16px; left: 50%; transform: translateX(-50%);
  font-size: 8px; color: ${NEON};
  letter-spacing: 2px;
  animation: kfb-dots 1.8s ease-in-out infinite;
}
@keyframes kfb-dots {
  0%   { opacity: 0;   transform: translateX(-50%) translateY(-2px); }
  25%  { opacity: 0.4; transform: translateX(-50%) translateY(0); }
  50%  { opacity: 0.7; }
  75%  { opacity: 1; }
  100% { opacity: 0;   transform: translateX(-50%) translateY(-2px); }
}
.kfb-thinking .kfb-antenna-ping {
  display: block;
  position: absolute; top: -6px; left: 50%;
  width: 5px; height: 5px; border-radius: 50%;
  border: 1.5px solid ${NEON};
  transform: translateX(-50%);
  animation: kfb-ping 1.4s ease-out infinite;
}
@keyframes kfb-ping {
  0%   { opacity: 0.7; transform: translateX(-50%) scale(0.6); }
  100% { opacity: 0;   transform: translateX(-50%) scale(3); }
}

/* ============== HAPPY ============== */
.kfb-happy {
  animation:
    kfb-happy-float 1.4s ease-in-out infinite,
    kfb-breathe 4.5s ease-in-out infinite;
  border-color: ${NEON};
  box-shadow: 0 0 26px rgba(0,255,153,0.07);
}
@keyframes kfb-happy-float {
  0%,100% { transform: translateY(0)    scale(1); }
  50%     { transform: translateY(-5px) scale(1.05); }
}
.kfb-happy .kfb-eye {
  height: 4px; border-radius: 8px 8px 0 0;
  background: transparent;
  border: 2px solid ${NEON}; border-bottom: none;
  box-shadow: none;
}
.kfb-happy .kfb-mouth {
  width: 16px; height: 6px; background: transparent;
  border: 1.5px solid ${NEON}; border-top: none;
  border-radius: 0 0 12px 12px;
}
.kfb-happy .kfb-blush {
  display: block;
  position: absolute;
  width: 6px; height: 3px; border-radius: 50%;
  background: ${PINK};
  bottom: 9px;
  filter: blur(0.5px);
}
.kfb-happy .kfb-blush-l { left: 5px; }
.kfb-happy .kfb-blush-r { right: 5px; }

/* ============== ALERT ============== */
.kfb-alert {
  animation: kfb-jolt 1.4s ease-in-out infinite;
  border-color: ${NEON};
  box-shadow: 0 0 20px rgba(0,255,153,0.07);
}
@keyframes kfb-jolt {
  0%,60%,100% { transform: translateY(0); }
  8%          { transform: translateY(-7px); }
  18%         { transform: translateY(0); }
  28%         { transform: translateY(-3px); }
  38%         { transform: translateY(0); }
}
.kfb-alert .kfb-eye { width: 9px; height: 9px; }

/* ============== ERROR ============== */
.kfb-error {
  border-color: ${RED};
  box-shadow: 0 0 14px rgba(255,77,77,0.06);
  animation: kfb-error-shake 2.4s ease-in-out infinite;
}
@keyframes kfb-error-shake {
  0%,100% { transform: translateX(0)  translateY(0); }
  25%     { transform: translateX(-3px) translateY(2px); }
  50%     { transform: translateX(0)  translateY(2px); }
  75%     { transform: translateX(3px) translateY(2px); }
}
.kfb-error .kfb-antenna {
  animation: none;
  transform: translateX(-50%) rotate(60deg);
}
.kfb-error .kfb-antenna-dot {
  animation: none;
  background: ${RED};
  opacity: 0.6;
}
.kfb-error .kfb-eye {
  width: 10px; height: 10px; background: transparent;
  box-shadow: none; position: relative;
}
.kfb-error .kfb-eye::before, .kfb-error .kfb-eye::after {
  content: ""; position: absolute; top: 50%; left: 50%;
  width: 12px; height: 1.5px; background: ${RED};
}
.kfb-error .kfb-eye::before { transform: translate(-50%, -50%) rotate( 45deg); }
.kfb-error .kfb-eye::after  { transform: translate(-50%, -50%) rotate(-45deg); }
.kfb-error .kfb-mouth {
  width: 14px; height: 5px; background: transparent;
  border: 1.5px solid ${RED}; border-top: none;
  border-radius: 12px 12px 0 0; transform: rotate(180deg);
}

/* ============== LOADING ============== */
.kfb-loading {
  animation:
    kfb-rock 1.6s ease-in-out infinite,
    kfb-bob 1.4s ease-in-out infinite;
}
@keyframes kfb-rock {
  0%,100% { rotate: -4deg; }
  50%     { rotate:  4deg; }
}
.kfb-loading .kfb-eye {
  animation: kfb-load-scan 2s ease-in-out infinite;
  width: 7px; height: 7px;
}
@keyframes kfb-load-scan {
  0%,100% { transform: translateX(0);    opacity: 1;   }
  25%     { transform: translateX(-3px); opacity: 0.9; }
  75%     { transform: translateX(3px);  opacity: 0.9; }
}
.kfb-loading .kfb-mouth { display: none; }

/* ============== LISTENING (typing detected) ============== */
.kfb-listening {
  transform: scale(1.04) translateY(-1px);
  border-color: ${NEON};
  box-shadow: 0 0 22px rgba(0,255,153,0.07);
  animation: kfb-breathe 4.5s ease-in-out infinite;
}
.kfb-listening .kfb-eye { width: 9px; height: 9px; }
.kfb-listening .kfb-mouth { width: 12px; }

/* ============== CELEBRATING ============== */
.kfb-celebrating {
  animation: kfb-celebrate 1.3s ease-out;
  border-color: ${NEON};
  box-shadow: 0 0 30px rgba(0,255,153,0.08);
}
@keyframes kfb-celebrate {
  0%   { transform: translateY(0)    scale(1); }
  25%  { transform: translateY(-8px) scale(1.10); }
  50%  { transform: translateY(0)    scale(1); }
  75%  { transform: translateY(-5px) scale(1.06); }
  100% { transform: translateY(0)    scale(1); }
}
.kfb-celebrating .kfb-eye {
  height: 4px; border-radius: 8px 8px 0 0;
  background: transparent;
  border: 2px solid ${NEON}; border-bottom: none;
  box-shadow: none;
}
.kfb-celebrating .kfb-mouth {
  width: 16px; height: 6px; background: transparent;
  border: 1.5px solid ${NEON}; border-top: none;
  border-radius: 0 0 12px 12px;
}

/* ============== EXCITED — welcome-screen entrance burst ============== */
.kfb-excited {
  border-color: ${NEON};
  box-shadow: 0 0 30px rgba(0,255,153,0.55);
  animation: kfb-excited-bounce 0.5s ease-in-out 3;
}
@keyframes kfb-excited-bounce {
  0%,100% { transform: translateY(0)    scale(1); }
  50%     { transform: translateY(-12px) scale(1.08); }
}
.kfb-excited .kfb-eye {
  width: 11px; height: 11px;
  background: radial-gradient(circle at 30% 30%, #ffffff 0%, #ffffff 25%, ${NEON} 60%);
  box-shadow: 0 0 10px rgba(0,255,153,0.7);
}
.kfb-excited .kfb-mouth {
  width: 18px; height: 7px; background: transparent;
  border: 1.5px solid ${NEON}; border-top: none;
  border-radius: 0 0 14px 14px;
}
.kfb-excited .kfb-antenna {
  animation: kfb-excited-wiggle 0.4s ease-in-out 5 !important;
}
@keyframes kfb-excited-wiggle {
  0%,100% { transform: translateX(-50%) rotate(0); }
  25%     { transform: translateX(-50%) rotate(-25deg); }
  75%     { transform: translateX(-50%) rotate(25deg); }
}
.kfb-excited .kfb-blush {
  display: block;
  position: absolute;
  width: 8px; height: 4px; border-radius: 50%;
  background: rgba(255,138,188,1);
  bottom: 8px;
  filter: blur(0.5px);
}
.kfb-excited .kfb-blush-l { left: 4px; }
.kfb-excited .kfb-blush-r { right: 4px; }

/* ============== PARTICLES ============== */
.kfb-sparkle {
  position: absolute; width: 4px; height: 4px;
  background: ${NEON}; border-radius: 50%;
  box-shadow: 0 0 6px rgba(0,255,153,0.12);
  pointer-events: none;
  animation: kfb-sparkle 1.6s ease-out infinite;
  z-index: 2;
}
.kfb-sparkle-1 { top: 4px; left: 8px;  animation-delay: 0s;   }
.kfb-sparkle-2 { top: 6px; right: 6px; animation-delay: 0.4s; }
.kfb-sparkle-3 { top: 0;   left: 50%;  animation-delay: 0.8s; }
@keyframes kfb-sparkle {
  0%   { opacity: 0; transform: translateY(0)    scale(0.5); }
  20%  { opacity: 1; transform: translateY(-4px)  scale(1);   }
  100% { opacity: 0; transform: translateY(-14px) scale(0.5); }
}

.kfb-heart {
  position: absolute;
  bottom: 14px; left: 50%; transform: translateX(-50%);
  color: ${PINK};
  font-size: 10px;
  text-shadow: 0 0 6px rgba(255,138,188,0.07);
  pointer-events: none; z-index: 2;
  animation: kfb-heart-rise 2.8s ease-out infinite;
}
@keyframes kfb-heart-rise {
  0%   { opacity: 0;   transform: translate(-50%, 0)    scale(0.7); }
  15%  { opacity: 1;   transform: translate(-50%, -10px) scale(1);   }
  80%  { opacity: 0.8; transform: translate(-50%, -36px) scale(1);   }
  100% { opacity: 0;   transform: translate(-50%, -50px) scale(0.6); }
}

.kfb-check {
  position: absolute;
  top: -22px; left: 50%; transform: translateX(-50%);
  color: ${NEON};
  font-size: 16px; font-weight: 700;
  text-shadow: 0 0 8px rgba(0,255,153,0.12);
  pointer-events: none; z-index: 2;
  animation: kfb-check-pop 1.3s ease-out;
}
@keyframes kfb-check-pop {
  0%   { opacity: 0; transform: translate(-50%, 6px)  scale(0.5); }
  25%  { opacity: 1; transform: translate(-50%, 0)    scale(1.2); }
  50%  { opacity: 1; transform: translate(-50%, 0)    scale(1);   }
  100% { opacity: 0; transform: translate(-50%, -4px) scale(0.9); }
}
`;
